import React, { useState, useEffect } from 'react';
import { X, Settings, Cookie } from 'lucide-react';

export const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Small delay to not show immediately on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const saved = JSON.parse(consent);
        setPreferences(saved);
      } catch (e) {
        setShowBanner(true);
      }
    }
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    setPreferences(prefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  const acceptAll = () => {
    savePreferences({
      necessary: true,
      analytics: true,
      functional: true,
    });
  };

  const acceptNecessary = () => {
    savePreferences({
      necessary: true,
      analytics: false,
      functional: false,
    });
  };

  const saveCustom = () => {
    savePreferences(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/50 z-[100]" onClick={() => {}} />
      
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-[101] p-4 md:p-6">
        <div className="max-w-4xl mx-auto bg-gray-900 border border-gray-700 rounded-xl shadow-2xl overflow-hidden">
          {!showSettings ? (
            // Main Banner
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#D4F7DC]/10 rounded-lg">
                  <Cookie className="w-6 h-6 text-[#D4F7DC]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-white mb-2">We value your privacy</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">
                    We use cookies to enhance your browsing experience, analyse site traffic, and personalise content. 
                    By clicking "Accept All", you consent to our use of cookies. You can customise your preferences 
                    or accept only necessary cookies.
                  </p>
                  <p className="text-gray-500 text-xs mb-4">
                    Read our <a href="/cookie-policy" className="text-[#D4F7DC] hover:underline">Cookie Policy</a> and <a href="/privacy-policy" className="text-[#D4F7DC] hover:underline">Privacy Policy</a> for more information.
                  </p>
                  
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={acceptAll}
                      className="px-6 py-2.5 bg-[#D4F7DC] text-[#2A2A2A] font-medium rounded-lg hover:bg-[#D4F7DC]/90 transition-colors"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={acceptNecessary}
                      className="px-6 py-2.5 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 transition-colors"
                    >
                      Necessary Only
                    </button>
                    <button
                      onClick={() => setShowSettings(true)}
                      className="px-6 py-2.5 border border-gray-600 text-gray-300 font-medium rounded-lg hover:border-[#D4F7DC] hover:text-[#D4F7DC] transition-colors flex items-center gap-2"
                    >
                      <Settings size={16} />
                      Customise
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Settings Panel
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Cookie Settings</h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-white">Strictly Necessary</h4>
                    <div className="px-3 py-1 bg-[#D4F7DC]/20 text-[#D4F7DC] text-xs rounded-full">
                      Always Active
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Essential for the website to function. These cannot be disabled.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-white">Analytics Cookies</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.analytics}
                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4F7DC]"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Help us understand how visitors interact with our website to improve user experience.
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="p-4 bg-gray-800 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-white">Functional Cookies</h4>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.functional}
                        onChange={(e) => setPreferences({ ...preferences, functional: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#D4F7DC]"></div>
                    </label>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Enable enhanced functionality such as chat widgets and personalised features.
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={saveCustom}
                  className="flex-1 px-6 py-2.5 bg-[#D4F7DC] text-[#2A2A2A] font-medium rounded-lg hover:bg-[#D4F7DC]/90 transition-colors"
                >
                  Save Preferences
                </button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="px-6 py-2.5 border border-gray-600 text-gray-300 font-medium rounded-lg hover:border-gray-500 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
